import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { selectFilteredStationData } from '../../redux/station-data/station-data.selectors';
import { selectWindToggleIsActive } from '../../redux/user/user.selectors';
import { setPollutantPopupType } from '../../redux/user/user.actions';

import { standardizeValue } from '../../redux/station-data/station-data.utils';

import { ARROW_RIGHT, CHEVRON } from '../../SVGs/svg.imports.js';

/**
 * The mimimum zoom level at which station data
 * should start appearing in the list view.
 */
const googleMapMinimumFilteredZoom = 7;

const ListView = ({ textContent, isHidden = true, isOverlay = false, filteredStationData, windToggleIsActive, setPopupType }) => {

  function createMarkup() {
    return {__html: textContent};
  }

  /**
   * Generate the CSS Class attribute values for the list view component
   *
   * @returns String
   */
  const generateComponentClasses = () => {
    let cssClasses = 'station-list-view__container';

    if (!isHidden) {
      cssClasses = cssClasses + ' is-visible';
    }

    if (isOverlay) {
      cssClasses = cssClasses + ' station-list-view__container--map-overlay';
    }

    return cssClasses;
  };

  /**
   * Respond to clicks on the "pollutant popup" buttons.
   * Trigger the pollutant popup to open
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onPopupButtonClick = (evt) => {
    evt.preventDefault();
    setPopupType(evt.currentTarget.getAttribute('data-popup-type'));
  };

  /**
   * Respond to clicks on the "Station List" button
   *
   * @param {Change Event} evt Input Event that triggered the change
   */
  const onAccordionButtonClick = (evt) => {
    const ariaState = evt.currentTarget.getAttribute('aria-expanded');
    evt.preventDefault();
    evt.currentTarget.classList.toggle("is-active");

    if (ariaState === 'true') {
      evt.currentTarget.setAttribute('aria-expanded', 'false')
    } else {
      evt.currentTarget.setAttribute('aria-expanded', 'true')
    }
  };

  return (

    <div className={generateComponentClasses()}>
      {
        (isOverlay) ? <button className="station-list-view-accordion__button is-active" onClick={onAccordionButtonClick} aria-expanded="true"><span className="visuallyhidden">Toggle list of </span>Stations found<span className="station-list-view-accordion__icon"><CHEVRON /></span></button> : null
      }
      <div className="station-list-view-accordion__content">
        {
          (filteredStationData.visibleMarkers.length === 0 || filteredStationData.currentMapZoom < googleMapMinimumFilteredZoom) ?
            (
              <div className="station-list-view-results__container">
                <div className="station-list-view__message-container" dangerouslySetInnerHTML={ createMarkup() } />
              </div>
            )
          :
          (
            <div className="station-list-view-results__container">
              <p className="station-list-view-results__message">
                Sorted by distance (from centre of map)
              </p>
              <div className="station-list-view-results__list-container">
                <ol className="station-list-view-results__list">
                {
                  (filteredStationData.visibleMarkers.length !== 0) ? filteredStationData.visibleMarkers.map( station => {
                    const stationUrl = "https://www.airquality.nsw.gov.au/";
                    let stationItemClasses = "station-list-view-results__list-item has-status-";

                    // Determine which colour scheme to use for the station
                    switch (station.AirQualityCategory) {
                      case "GOOD":
                        stationItemClasses += "good";
                        break;

                      case "FAIR":
                        stationItemClasses += "fair";
                        break;

                      case "POOR":
                        stationItemClasses += "poor";
                        break;

                      case "VERY POOR":
                        stationItemClasses += "very-poor";
                        break;

                      case "EXTREMELY POOR":
                        stationItemClasses += "extremely-poor";
                        break;

                        case "Inactive":
                        stationItemClasses += "inactive";
                        break;

                      default:
                        break;
                    }

                    return (
                      <li key={station.id} className={stationItemClasses}>
                        <a href={ stationUrl + station.Site_Id } className="station-list-view-results-location__summary">
                          <div className="station-list-view-results-location__details">
                            <span className="station-list-view-results-location__name">{station.SiteName}</span>
                            <span className="station-list-view-results-location__distance">{ Math.round(station.RelativeDistanceToOrigin) } km</span>
                            <span className="station-list-view-results-location__status">{station.AirQualityCategory}</span>
                            {
                              (station.SitePurpose !== "" ?
                                <span className="station-list-view-results-location__purpose">{station.SitePurpose}</span>
                                : null)
                            }
                          </div>
                          <div className="station-list-view-results-location__icon">
                            <ARROW_RIGHT />
                          </div>
                        </a>
                        {
                          (station.DeterminingPollutantValue !== null) ?
                            <div className="station-list-view-results-location__data">
                              <span className="station-list-view-results-location__status-icon"></span>
                              <div className="station-list-view-results-location__data-details">
                                <button onClick={onPopupButtonClick} data-popup-type={station.DeterminingPollutant} className="station-list-view-results-location__data-unit station-list-view-results-location__data-button" aria-haspopup="dialog">
                                  {
                                    (station.DeterminingPollutant.toLowerCase() !== "ozone") ?
                                      `Particles (${station.DeterminingPollutant})`
                                    : station.DeterminingPollutant
                                  }
                                </button>
                                <div className="station-list-view-results-location__data-frequency">
                                  Hourly
                                </div>
                              </div>
                              <div className="station-list-view-results-location__data-value">
                                { standardizeValue(station.DeterminingPollutantValue) } {station.DeterminingPollutantUnit}
                              </div>
                            </div>
                          : null
                        }
                        {
                          (windToggleIsActive && station.WindDirectionDegrees !== null) ? (
                            <div className="station-list-view-results-location__data">
                              <div className="station-list-view-results-location__data-details">
                                <div className="station-list-view-results-location__data-unit">
                                  Wind
                                </div>
                              </div>
                              <div className="station-list-view-results-location__data-value">
                                {station.WindDirectionCompass} {station.WindDirectionDegrees}&deg; { (station.WindSpeed !== null ? `${ standardizeValue(station.WindSpeed) }m/s` : null) }
                              </div>
                            </div>
                          ) : null
                        }
                      </li>
                    );
                  }) : null
                }
                </ol>
                <p className="station-list-view-results__instructions">
                  To view more stations use the map to zoom in or the search function to find stations near you.
                </p>
              </div>
            </div>
          )
        }
      </div>
    </div>
  );
};

ListView.propTypes = {
  "textContent": PropTypes.string,
  "isHidden": PropTypes.bool,
  "isOverlay": PropTypes.bool,
  "filteredStationData": PropTypes.object,
  "windToggleIsActive": PropTypes.bool,
  "setPopupType": PropTypes.oneOfType( [PropTypes.string, PropTypes.func] )
};

const mapStateToProps = (state) => {
  return {
    "filteredStationData": selectFilteredStationData(state),
    "windToggleIsActive": selectWindToggleIsActive(state)
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    "setPopupType": popupTypeId => dispatch(setPollutantPopupType(popupTypeId))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ListView);